import React, { useEffect, useState } from 'react'
import { Container, Nav, Tab } from 'react-bootstrap'
import { useQueryClient } from '@tanstack/react-query'

import './ChessApiAnalytics.scss'
import { fetchChessApi, ChessApiMetric, ChessApiMonthEntry } from './ChessApi'
import CDropdown from '../../components/Common/CDropdown/CDropdown'
import { MONTHS } from '../../constants'
import { Table as TableIcon, BarChartFill } from 'react-bootstrap-icons'
import ChessApiTable from './ChessApiTable'
import ChessApiChart from './ChessApiChart'

export const calculateAvgLast10Time = (last10Time?: number[]): string => {
  const total = last10Time?.reduce((sum, time) => sum + time, 0) ?? 0
  return (total / (last10Time?.length || 1)).toFixed(2)
}

export const formatApiName = (name: string): string => {
  if (name.startsWith('chessx-')) {
    return name.replace('chessx-', '')
  }
  return name
}

interface DropdownOption {
  name: string
  value: string
}

interface DurationState {
  month?: DropdownOption
  year?: DropdownOption
}

const ChessApiAnalytics = () => {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<string | null>('table')
  const [duration, setDuration] = useState<DurationState | null>(null)
  const [filteredData, setFilteredData] = useState<Record<string, ChessApiMetric> | null>(null)
  const [dropDownData, setDropDownData] = useState<{
    months: DropdownOption[]
    years: DropdownOption[]
  }>({ months: [], years: [] })

  useEffect(() => {
    return () => {
      queryClient.cancelQueries({ queryKey: ['CHESS_API'] })
    }
  }, [])

  const { isLoading, data } = fetchChessApi()

  React.useEffect(() => {
    if (data) {
      const date = new Date()
      const month = (date.getMonth() + 1).toString()
      const year = date.getFullYear().toString()
      const months = MONTHS.filter(m => {
        return data.find((item: ChessApiMonthEntry) => item.month === m.value && item.year === year)
      }).sort((a, b) => Number(b.value) - Number(a.value))

      const years =
        data &&
        Array.from(new Set(data.map((item: ChessApiMonthEntry) => item.year)))
          ?.sort((a, b) => Number(b) - Number(a))
          ?.map(y => ({ name: String(y), value: String(y) }))

      setDropDownData({ months, years })
      setDuration({
        month: MONTHS.find(item => item.value === month),
        year: { name: year, value: year }
      })
    }
  }, [data])

  React.useEffect(() => {
    if (duration && data?.length) {
      const filterdData = data.find(
        (item: ChessApiMonthEntry) =>
          item.month === duration.month?.value && item.year === duration.year?.value
      )
      setFilteredData(filterdData ? filterdData.meta : null)
    }
  }, [duration])

  if (isLoading) {
    return <div>Loading...</div>
  }

  const handleTabChange = (tabKey: string | null) => {
    setActiveTab(tabKey)
  }

  const handleMonthChange = (month: DropdownOption) => {
    setDuration({ ...duration, month })
  }
  const handleYearChange = (year: DropdownOption) => {
    const months = MONTHS.filter(m => {
      return data?.find(
        (item: ChessApiMonthEntry) => item.month === m.value && item.year === year.value
      )
    }).sort((a, b) => Number(b.value) - Number(a.value))

    setDropDownData({ months, years: dropDownData.years })
    const findMonth = months.findIndex(item => item.value === duration?.month?.value)
    setDuration({ month: findMonth !== -1 ? duration?.month : months[0], year })
  }
  return (
    <Container className="api-container">
      <div className="header-container">
        <div className="title">CHESS ENGINE</div>
        <div className="duration-container">
          <div className="month-container">
            <CDropdown
              data={dropDownData.months}
              onSelect={item => handleMonthChange(item as DropdownOption)}
              selectedItem={duration?.month}
            />
          </div>
          <CDropdown
            data={dropDownData.years}
            onSelect={item => handleYearChange(item as DropdownOption)}
            selectedItem={duration?.year}
          />
        </div>
      </div>
      {filteredData ? (
        <Tab.Container activeKey={activeTab ?? undefined} onSelect={handleTabChange}>
          <Nav variant="pills" className="justify-content-end tab-bar">
            <Nav.Item className="mt-2">
              <Nav.Link eventKey="table" className={activeTab === 'table' ? 'active' : ''}>
                <TableIcon /> Table
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className="mt-2">
              <Nav.Link eventKey="chart" className={activeTab === 'chart' ? 'active' : ''}>
                <BarChartFill /> Chart
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <Tab.Content>
            <Tab.Pane eventKey="table">
              <ChessApiTable data={filteredData} />
            </Tab.Pane>
            <Tab.Pane eventKey="chart">
              <ChessApiChart data={filteredData} />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      ) : (
        <div>No data found</div>
      )}
    </Container>
  )
}
export default ChessApiAnalytics
